import React, { useCallback, useEffect, useState } from "react";
import { LayoutDashboard, Route as RouteIcon, Map, History, User, Plus, Edit2, Trash2, ArrowRight, Calendar, Users2, Star } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import { useAuth } from "@/context/AuthContext";
import { driverApi } from "@/lib/driverApi";

const navItems = [
  { to: "/conductor", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/conductor/ruta", label: "Ruta optimizada", icon: RouteIcon },
  { to: "/conductor/rutas-tarifas", label: "Mis rutas", icon: Map },
  { to: "/conductor/historial", label: "Historial", icon: History },
  { to: "/conductor/calificaciones", label: "Calificaciones", icon: Star },
  { to: "/conductor/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/conductor", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/conductor/ruta", label: "Ruta", icon: RouteIcon },
  { to: "/conductor/rutas-tarifas", label: "Rutas", icon: Map },
  { to: "/conductor/historial", label: "Viajes", icon: History },
  { to: "/conductor/perfil", label: "Perfil", icon: User },
];

const DIRECTION_LABELS = {
  aeropuerto_ciudad: "Aeropuerto → Ciudad",
  ciudad_aeropuerto: "Ciudad → Aeropuerto",
};
const DAY_LABELS = { 1: "Lunes", 2: "Martes", 3: "Miércoles", 4: "Jueves", 5: "Viernes", 6: "Sábado", 7: "Domingo" };

const EMPTY_ROUTE = {
  airport_name: "Aeropuerto Jorge Chávez",
  city: "",
  direction: "aeropuerto_ciudad",
  estimated_duration_minutes: "",
  estimated_distance_km: "",
  private_enabled: true,
  private_price: "",
  available_from: "06:00",
  available_until: "22:00",
  shared_enabled: false,
  price_per_seat: "",
};

const DriverRoutesManage = () => {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_ROUTE);
  const [saving, setSaving] = useState(false);
  const [schedulesRouteId, setSchedulesRouteId] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [newSchedule, setNewSchedule] = useState({ day_of_week: 1, departure_time: "08:00" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await driverApi.listRoutes();
      setRoutes(data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Primero completa tu perfil de conductor antes de agregar rutas.");
      } else {
        setError("No se pudieron cargar tus rutas.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_ROUTE);
    setOpenForm(true);
  };

  const startEdit = (route) => {
    setEditingId(route.id);
    setForm({
      airport_name: route.airport_name,
      city: route.city,
      direction: route.direction,
      estimated_duration_minutes: route.estimated_duration_minutes,
      estimated_distance_km: route.estimated_distance_km,
      private_enabled: route.private_enabled,
      private_price: route.private_price || "",
      available_from: route.available_from ? route.available_from.slice(0, 5) : "06:00",
      available_until: route.available_until ? route.available_until.slice(0, 5) : "22:00",
      shared_enabled: route.shared_enabled,
      price_per_seat: route.price_per_seat || "",
    });
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const payload = {
      airport_name: form.airport_name,
      city: form.city,
      direction: form.direction,
      estimated_duration_minutes: Number(form.estimated_duration_minutes),
      estimated_distance_km: String(form.estimated_distance_km),
      private_enabled: form.private_enabled,
      shared_enabled: form.shared_enabled,
    };
    if (form.private_enabled) {
      payload.private_price = String(form.private_price);
      payload.available_from = `${form.available_from}:00`;
      payload.available_until = `${form.available_until}:00`;
    }
    if (form.shared_enabled) {
      payload.price_per_seat = String(form.price_per_seat);
    }
    try {
      if (editingId) {
        await driverApi.updateRoute(editingId, payload);
      } else {
        await driverApi.createRoute(payload);
      }
      setOpenForm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || (Array.isArray(err.response?.data?.detail) ? err.response.data.detail[0]?.msg : "No se pudo guardar la ruta. Revisa los datos."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (routeId) => {
    setError("");
    try {
      await driverApi.deleteRoute(routeId);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo eliminar la ruta.");
    }
  };

  const toggleSchedules = async (routeId) => {
    if (schedulesRouteId === routeId) {
      setSchedulesRouteId(null);
      return;
    }
    setSchedulesRouteId(routeId);
    const { data } = await driverApi.listSchedules(routeId);
    setSchedules(data);
  };

  const addSchedule = async (routeId) => {
    setError("");
    try {
      await driverApi.createSchedule(routeId, {
        day_of_week: Number(newSchedule.day_of_week),
        departure_time: `${newSchedule.departure_time}:00`,
      });
      const { data } = await driverApi.listSchedules(routeId);
      setSchedules(data);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo crear el horario.");
    }
  };

  const removeSchedule = async (routeId, scheduleId) => {
    setError("");
    try {
      await driverApi.deactivateSchedule(routeId, scheduleId);
      const { data } = await driverApi.listSchedules(routeId);
      setSchedules(data);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo quitar el horario.");
    }
  };

  return (
    <PanelShell role="Conductor" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="driver-routes">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Catálogo</p>
            <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Mis rutas y tarifas</h1>
          </div>
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
            data-testid="new-route-btn"
          >
            <Plus size={14} /> Nueva ruta
          </button>
        </div>

        {error && (
          <p className="mb-4 text-xs text-red-400 font-body" data-testid="driver-routes-error">
            {error}
          </p>
        )}

        {openForm && (
          <div className="mb-6 bg-[#1C1D20] border border-[#C9A227]/40 rounded-xl p-6" data-testid="route-form">
            <h3 className="font-display font-bold text-lg uppercase tracking-wide mb-6">{editingId ? "Editar ruta" : "Nueva ruta"}</h3>
            <form onSubmit={submit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Aeropuerto" value={form.airport_name} onChange={(e) => setForm((f) => ({ ...f, airport_name: e.target.value }))} required />
                <Field label="Ciudad" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} required />
                <div>
                  <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">Sentido</label>
                  <select
                    value={form.direction}
                    onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value }))}
                    className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none"
                  >
                    {Object.entries(DIRECTION_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <Field label="Duración estimada (min)" type="number" value={form.estimated_duration_minutes} onChange={(e) => setForm((f) => ({ ...f, estimated_duration_minutes: e.target.value }))} required />
                <Field label="Distancia estimada (km)" type="number" step="0.1" value={form.estimated_distance_km} onChange={(e) => setForm((f) => ({ ...f, estimated_distance_km: e.target.value }))} required />
              </div>

              <div className="mt-6 pt-6 border-t border-[#2E2F33]">
                <label className="flex items-center gap-2 text-xs text-[#F5F5F5] font-body mb-4">
                  <input type="checkbox" checked={form.private_enabled} onChange={(e) => setForm((f) => ({ ...f, private_enabled: e.target.checked }))} className="accent-[#C9A227]" data-testid="toggle-private" />
                  Modalidad privada (vehículo completo, hora libre)
                </label>
                {form.private_enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field label="Tarifa (S/)" type="number" step="0.01" value={form.private_price} onChange={(e) => setForm((f) => ({ ...f, private_price: e.target.value }))} required />
                    <Field label="Disponible desde" type="time" value={form.available_from} onChange={(e) => setForm((f) => ({ ...f, available_from: e.target.value }))} required />
                    <Field label="Disponible hasta" type="time" value={form.available_until} onChange={(e) => setForm((f) => ({ ...f, available_until: e.target.value }))} required />
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-[#2E2F33]">
                <label className="flex items-center gap-2 text-xs text-[#F5F5F5] font-body mb-4">
                  <input type="checkbox" checked={form.shared_enabled} onChange={(e) => setForm((f) => ({ ...f, shared_enabled: e.target.checked }))} className="accent-[#C9A227]" data-testid="toggle-shared" />
                  Modalidad compartida (por asiento, horario fijo)
                </label>
                {form.shared_enabled && (
                  <Field label="Tarifa por asiento (S/)" type="number" step="0.01" value={form.price_per_seat} onChange={(e) => setForm((f) => ({ ...f, price_per_seat: e.target.value }))} required />
                )}
              </div>

              <div className="mt-6 flex items-center gap-3 justify-end">
                <button type="button" onClick={() => setOpenForm(false)} className="px-5 py-2.5 border border-[#2E2F33] text-[#A0A0A5] hover:border-[#C9A227] uppercase tracking-wider font-display text-xs rounded-lg">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#C9A227] hover:bg-[#D4AF37] disabled:opacity-60 text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
                  data-testid="save-route-btn"
                >
                  {saving ? "Guardando…" : "Guardar ruta"}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="space-y-3">
          {!loading &&
            routes.map((r) => (
              <div key={r.id} className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl overflow-hidden" data-testid={`driver-route-row-${r.id}`}>
                <div className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-[#F5F5F5] font-body">
                        {r.direction === "aeropuerto_ciudad" ? r.airport_name : r.city}
                      </span>
                      <ArrowRight size={12} className="text-[#C9A227] shrink-0" />
                      <span className="text-sm text-[#F5F5F5] font-body">{r.direction === "aeropuerto_ciudad" ? r.city : r.airport_name}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {r.private_enabled && (
                        <span className="px-2 py-0.5 border border-[#2E2F33] rounded-full text-[10px] uppercase tracking-wider text-[#A0A0A5] font-display">
                          Privado · S/ {r.private_price}
                        </span>
                      )}
                      {r.shared_enabled && (
                        <span className="px-2 py-0.5 border border-[#C9A227]/40 rounded-full text-[10px] uppercase tracking-wider text-[#C9A227] font-display">
                          Compartido · S/ {r.price_per_seat}/asiento
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.shared_enabled && (
                      <button onClick={() => toggleSchedules(r.id)} className="p-1.5 text-[#A0A0A5] hover:text-[#C9A227]" data-testid={`toggle-schedules-${r.id}`} title="Horarios">
                        <Calendar size={14} />
                      </button>
                    )}
                    <button onClick={() => startEdit(r)} className="p-1.5 text-[#A0A0A5] hover:text-[#C9A227]" data-testid={`edit-driver-route-${r.id}`}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => remove(r.id)} className="p-1.5 text-[#A0A0A5] hover:text-red-400" data-testid={`delete-driver-route-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {schedulesRouteId === r.id && (
                  <div className="px-5 pb-5 pt-2 border-t border-[#2E2F33]" data-testid={`schedules-panel-${r.id}`}>
                    <div className="flex items-center gap-2 mb-3 text-[#A0A0A5]">
                      <Users2 size={13} />
                      <p className="text-[10px] uppercase tracking-wider font-display">Horarios recurrentes</p>
                    </div>
                    <div className="space-y-2 mb-4">
                      {schedules.length === 0 && <p className="text-xs text-[#A0A0A5] font-body">Sin horarios todavía.</p>}
                      {schedules.map((s) => (
                        <div key={s.id} className="flex items-center justify-between bg-[#0D0E10] border border-[#2E2F33] rounded-lg px-4 py-2" data-testid={`schedule-${s.id}`}>
                          <span className="text-xs text-[#F5F5F5] font-body">
                            {DAY_LABELS[s.day_of_week]} · {s.departure_time.slice(0, 5)}
                          </span>
                          <button onClick={() => removeSchedule(r.id, s.id)} className="text-[10px] text-[#A0A0A5] hover:text-red-400 uppercase tracking-wider font-display" data-testid={`remove-schedule-${s.id}`}>
                            Quitar
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-end gap-3 flex-wrap">
                      <div>
                        <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-1">Día</label>
                        <select
                          value={newSchedule.day_of_week}
                          onChange={(e) => setNewSchedule((s) => ({ ...s, day_of_week: e.target.value }))}
                          className="bg-[#0D0E10] border border-[#2E2F33] rounded-lg px-3 py-2 text-xs text-[#F5F5F5] font-body outline-none"
                        >
                          {Object.entries(DAY_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-1">Hora</label>
                        <input
                          type="time"
                          value={newSchedule.departure_time}
                          onChange={(e) => setNewSchedule((s) => ({ ...s, departure_time: e.target.value }))}
                          className="bg-[#0D0E10] border border-[#2E2F33] rounded-lg px-3 py-2 text-xs text-[#F5F5F5] font-body outline-none"
                        />
                      </div>
                      <button
                        onClick={() => addSchedule(r.id)}
                        className="px-4 py-2 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-[10px] rounded-lg"
                        data-testid={`add-schedule-${r.id}`}
                      >
                        Agregar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          {!loading && routes.length === 0 && <p className="text-xs text-[#A0A0A5] font-body">Todavía no registraste ninguna ruta.</p>}
        </div>
      </div>
    </PanelShell>
  );
};

const Field = ({ label, ...props }) => (
  <div>
    <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">{label}</label>
    <input {...props} className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none transition-colors" />
  </div>
);

export default DriverRoutesManage;
